// src/components/lead/LeadWorkspace.tsx
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { DirtyFormPromptProvider } from "@/contexts/DirtyFormPromptContext";
import { DirtyFormPromptModal } from "@/components/shell/DirtyFormPromptModal";
import { WorkplaceStreamChatSessionProvider } from "@/contexts/WorkplaceStreamChatSessionContext";
import { LeadWorkspaceShell } from "./LeadWorkspaceShell";
import { useLeadWorkspaceShellProps } from "./useLeadWorkspaceShellProps";

/**
 * Hard-reset the workspace subtree when the user switches organizations.
 * Keying the inner mount by `scopedOrganizationId` forces React to unmount
 * + remount the entire shell — including the TrainingPanel's pending KB
 * questions, the lead list, active recordings, transcript draft, and every
 * other component-local piece of state held inside the shell hook tree.
 *
 * Without this, transient UI state (most visibly: the training panel's
 * in-flight ask + answer) carries over from org A to org B until the user
 * navigates away, which is the cross-org contamination risk called out in
 * dev-sync note 06d93eb5. The user explicitly endorsed a full app reset on
 * org change as the right tradeoff vs. surgical per-feature reset wiring.
 */
export function LeadWorkspace() {
  const scopedOrgId = useScopedOrganizationId();
  return (
    // DirtyFormPromptProvider wraps the workspace shell so cross-cutting
    // forms (Org Settings, future admin-bucket panels) can register their
    // dirty state and the bucket/mode rail can intercept navigation when
    // changes are pending. See docs/sql_schema_opportunity.md is unrelated
    // — this is the unsaved-changes guard from opp engine iter 2 slice A.
    // Provider mounts here (above the org-scoped re-key) so the modal +
    // beforeunload listener survive an org swap; the registered forms
    // unmount with the shell and re-register fresh on the new org.
    <DirtyFormPromptProvider
      renderModal={(state, actions) => (
        <DirtyFormPromptModal
          open={state.open}
          dirtyEntries={state.open ? state.dirtyEntries : []}
          onCancel={actions.onCancel}
          onDiscard={actions.onDiscard}
          onSaveAndContinue={actions.onSaveAndContinue}
        />
      )}
    >
      <LeadWorkspaceShellHost key={scopedOrgId ?? "no-org"} />
    </DirtyFormPromptProvider>
  );
}

function LeadWorkspaceShellHost() {
  // WC-V20-HOST: Stream session lives inside the org-keyed remount so mode
  // switches keep the connection, while org switch / full refresh tear it down.
  return (
    <WorkplaceStreamChatSessionProvider>
      <LeadWorkspaceShell {...useLeadWorkspaceShellProps()} />
    </WorkplaceStreamChatSessionProvider>
  );
}
